import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { AppModeProvider } from './contexts/AppModeContext';
import { IndustryConfigProvider } from './contexts/IndustryConfigContext';
import PrivateRoute from './components/PrivateRoute';
import Layout from './components/Layout';
import InstallPWAPrompt from './components/InstallPWAPrompt';

// Lazy load pages for better performance
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const ForcePasswordChange = lazy(() => import('./pages/ForcePasswordChange'));
const SetupWizard = lazy(() => import('./pages/SetupWizard'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Contacts = lazy(() => import('./pages/Contacts'));
const ContactDetail = lazy(() => import('./pages/ContactDetail'));
const Companies = lazy(() => import('./pages/Companies'));
const CompanyDetail = lazy(() => import('./pages/CompanyDetail'));
const Profile = lazy(() => import('./pages/Profile'));
const CustomFields = lazy(() => import('./pages/CustomFields'));
const Pipeline = lazy(() => import('./pages/Pipeline'));
const Automations = lazy(() => import('./pages/Automations'));
const AutomationBuilder = lazy(() => import('./pages/AutomationBuilder'));
const WorkflowBuilder = lazy(() => import('./pages/WorkflowBuilder'));
const Metrics = lazy(() => import('./pages/EmailAnalytics'));
const ActivityReports = lazy(() => import('./pages/ActivityReports'));
const DuplicateContacts = lazy(() => import('./pages/DuplicateContacts'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const LocationManagement = lazy(() => import('./pages/LocationManagement'));
const OrganizationSettings = lazy(() => import('./pages/OrganizationSettings'));
const DeduplicationSettings = lazy(() => import('./pages/DeduplicationSettings'));

// Super Admin pages
const SuperAdminLayout = lazy(() => import('./components/SuperAdminLayout'));
const SuperAdminDashboard = lazy(() => import('./pages/SuperAdminDashboard'));
const OrganizationsManagement = lazy(() => import('./pages/OrganizationsManagement'));
const CreateOrganization = lazy(() => import('./pages/CreateOrganization'));
const EditOrganization = lazy(() => import('./pages/EditOrganization'));
const OrganizationDetail = lazy(() => import('./pages/OrganizationDetail'));
const OrganizationUsers = lazy(() => import('./pages/OrganizationUsers'));
const GlobalUsersView = lazy(() => import('./pages/GlobalUsersView'));
const IndustriesManagement = lazy(() => import('./pages/IndustriesManagement'));
const IndustryDetail = lazy(() => import('./pages/IndustryDetail'));

// Email Template pages
const EmailTemplates = lazy(() => import('./pages/EmailTemplates'));
const EmailTemplateEditor = lazy(() => import('./pages/EmailTemplateEditor'));

// Round-Robin pages
const AssignmentDashboard = lazy(() => import('./pages/RoundRobin/AssignmentDashboard'));
const AssignmentRules = lazy(() => import('./pages/RoundRobin/AssignmentRules'));
const RuleBuilder = lazy(() => import('./pages/RoundRobin/RuleBuilder'));
const ManualAssignment = lazy(() => import('./pages/RoundRobin/ManualAssignment'));
const AssignmentHistory = lazy(() => import('./pages/RoundRobin/AssignmentHistory'));

// Reminders page
const Reminders = lazy(() => import('./pages/Reminders'));

// Support Portal
const SupportPortal = lazy(() => import('./pages/Support/SupportPortal'));

// Form Builder pages
const Forms = lazy(() => import('./pages/Forms'));
const FormBuilder = lazy(() => import('./pages/FormBuilder'));
const PublicForm = lazy(() => import('./pages/PublicForm'));
const PublicProfilePage = lazy(() => import('./pages/PublicProfilePage'));
const FormSubmissions = lazy(() => import('./pages/FormSubmissions'));

// Tag Management
const TagManagement = lazy(() => import('./pages/TagManagement'));

// Contact Groups
const ContactGroups = lazy(() => import('./pages/ContactGroups'));
const ContactGroupDetail = lazy(() => import('./pages/ContactGroupDetail'));

// Broadcast History
const BroadcastHistory = lazy(() => import('./pages/BroadcastHistory'));

// Email Drafts — composed mail awaiting review and scheduling
const EmailDrafts = lazy(() => import('./pages/EmailDrafts'));

// Refinance Opportunities
const Opportunities = lazy(() => import('./pages/Opportunities'));

// Lender Playbook
const PlaybookSettings = lazy(() => import('./pages/PlaybookSettings'));

// Onboarding pages
const Onboarding = lazy(() => import('./pages/Onboarding'));
const OnboardingDetail = lazy(() => import('./pages/OnboardingDetail'));
const OnboardingTemplates = lazy(() => import('./pages/OnboardingTemplates'));

// Loading component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

function App() {
  return (
    <Router>
      <AuthProvider>
        <ThemeProvider>
          <IndustryConfigProvider>
            <AppModeProvider>
            <Suspense fallback={<PageLoader />}>
            <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register/:industrySlug" element={<Register />} />
            <Route path="/register" element={<Register />} />
            {/* Public pages (no auth required) */}
            <Route path="/f/:slug" element={<PublicForm />} />
            <Route path="/u/:slug" element={<PublicProfilePage />} />

            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/force-password-change" element={<PrivateRoute><ForcePasswordChange /></PrivateRoute>} />
            <Route path="/setup/*" element={<PrivateRoute><SetupWizard /></PrivateRoute>} />

            {/* Super Admin Routes */}
            <Route path="/super-admin" element={<PrivateRoute><SuperAdminLayout /></PrivateRoute>}>
              <Route index element={<SuperAdminDashboard />} />
              <Route path="organizations" element={<OrganizationsManagement />} />
              <Route path="organizations/new" element={<CreateOrganization />} />
              <Route path="organizations/:id" element={<OrganizationDetail />} />
              <Route path="organizations/:id/edit" element={<EditOrganization />} />
              <Route path="organizations/:id/users" element={<OrganizationUsers />} />
              <Route path="industries" element={<IndustriesManagement />} />
              <Route path="industries/:id" element={<IndustryDetail />} />
              <Route path="users" element={<GlobalUsersView />} />
              <Route path="analytics" element={<div>Analytics Dashboard - TODO</div>} />
            </Route>

            {/* Main App Routes */}
            <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="contacts" element={<Contacts />} />
              <Route path="contacts/:id" element={<ContactDetail />} />
              <Route path="companies" element={<Companies />} />
              <Route path="companies/:id" element={<CompanyDetail />} />
              <Route path="pipeline" element={<Pipeline />} />
              <Route path="automations" element={<Automations />} />
              <Route path="automations/:id" element={<AutomationBuilder />} />
              <Route path="automations/workflow/:id" element={<WorkflowBuilder />} />
              <Route path="email-analytics" element={<Metrics />} />
              <Route path="activity-reports" element={<ActivityReports />} />
              <Route path="profile" element={<Profile />} />
              <Route path="custom-fields" element={<CustomFields />} />
              <Route path="duplicates" element={<DuplicateContacts />} />
              <Route path="users" element={<UserManagement />} />
              <Route path="locations" element={<LocationManagement />} />
              <Route path="organization-settings" element={<OrganizationSettings />} />
              <Route path="deduplication-settings" element={<DeduplicationSettings />} />
              <Route path="tags" element={<TagManagement />} />
              <Route path="groups" element={<ContactGroups />} />
              <Route path="groups/:id" element={<ContactGroupDetail />} />
              <Route path="reminders" element={<Reminders />} />
              <Route path="opportunities" element={<Opportunities />} />
              <Route path="onboarding" element={<Onboarding />} />
              <Route path="onboarding/templates" element={<OnboardingTemplates />} />
              <Route path="onboarding/:id" element={<OnboardingDetail />} />
              <Route path="round-robin" element={<AssignmentDashboard />} />
              <Route path="round-robin/rules" element={<AssignmentRules />} />
              <Route path="round-robin/rules/new" element={<RuleBuilder />} />
              <Route path="round-robin/assign" element={<ManualAssignment />} />
              <Route path="round-robin/history" element={<AssignmentHistory />} />
              <Route path="broadcast-history" element={<BroadcastHistory />} />
              <Route path="email-drafts" element={<EmailDrafts />} />
              <Route path="email-templates" element={<EmailTemplates />} />
              <Route path="email-templates/new" element={<EmailTemplateEditor />} />
              <Route path="email-templates/:id" element={<EmailTemplateEditor />} />
              <Route path="email-templates/:id/preview" element={<EmailTemplateEditor />} />
              <Route path="forms" element={<Forms />} />
              <Route path="forms/new" element={<FormBuilder />} />
              <Route path="forms/:id" element={<FormBuilder />} />
              <Route path="forms/analytics" element={<FormSubmissions />} />
              <Route path="forms/:id/submissions" element={<FormSubmissions />} />
              <Route path="playbook-settings" element={<PlaybookSettings />} />
              <Route path="support" element={<SupportPortal />} />
            </Route>
            </Routes>
          </Suspense>
          <InstallPWAPrompt />
            </AppModeProvider>
          </IndustryConfigProvider>
        </ThemeProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;